/**
 * 把合成计划编译成 Render Graph（M1-12 / DESIGN.md §4.4）。
 *
 * §4.4 把合成顺序写成了八步，并且特意加了一句「顺序错了结果就错」。
 * 这个模块的全部意义就是把那八步**变成图的结构**——顺序错了不再表现为
 * "某张图看着有点怪"，而是拓扑序对不上，测试当场就红。
 *
 * 八步（自下而上，对每个图层）：
 *
 * ```
 * 1. 取图层像素        → SourceTile
 * 2. 智能滤镜链        → Effect(filter)
 * 3. fillOpacity      → Adjustment('fillOpacity')     ← 只影响像素
 * 4. 图层样式          → Effect(inner/outer)
 * 5. mask / vectorMask → Mask
 * 6. blendIf          → Adjustment('blendIf')
 * 7. clipped          → Clip（与下方基底的 alpha 相交）
 * 8. blendMode+opacity → Blend                        ← opacity 在这里
 * ```
 *
 * **opacity 与 fillOpacity 的位置差别**是第 3 步与第 8 步的距离：中间隔着图层样式。
 * 所以把 fillOpacity 调到 0，图层像素没了但投影还在；把 opacity 调到 0，两者一起没。
 * 很多实现把这两个当成一回事，那是错的。
 *
 * 与 graph.ts 一样，本模块**不认识文档模型**——分层规则不允许 gpu 层 import
 * document/engine。输入是一份「已经解析好的合成计划」，由上层翻译。
 */
import { RenderGraph, InvalidGraphError } from './graph';
import { nodeId, type NodeId, type RenderNode } from './node';

/** 合成计划里的图层公共字段。 */
export interface PlanLayerBase {
  readonly id: string;
  readonly visible: boolean;
  /** 0–1，作用于图层与其效果的整体（第 8 步）。 */
  readonly opacity: number;
  /** 0–1，**只影响像素、不影响图层样式**（第 3 步）。 */
  readonly fillOpacity: number;
  readonly blendMode: string;
  /** 剪贴到下方基底图层（第 7 步）。 */
  readonly clipped: boolean;
  /** 智能滤镜链（第 2 步）。 */
  readonly filters?: readonly string[];
  /** 图层样式（第 4 步）。 */
  readonly effects?: readonly { readonly name: string; readonly placement: 'inner' | 'outer' }[];
  /** 遮罩图层标识（第 5 步）。 */
  readonly mask?: string;
  /** 混合颜色带（第 6 步）。 */
  readonly blendIf?: boolean;
}

export interface PlanRasterLayer extends PlanLayerBase {
  readonly kind: 'raster';
}

export interface PlanAdjustmentLayer extends PlanLayerBase {
  readonly kind: 'adjustment';
  readonly adjustment: string;
  readonly params?: Readonly<Record<string, number>>;
}

export interface PlanGroupLayer extends PlanLayerBase {
  readonly kind: 'group';
  /**
   * 穿透模式。
   *
   * 穿透时组内图层直接混进**组外的背景**，组内的调整图层因此会影响到组外的内容；
   * 非穿透时组先在独立缓冲里合成，再整体混合——调整图层只能影响组内。
   * 这是语义差别，不是性能选项（§4.4）。
   */
  readonly passThrough: boolean;
  readonly children: readonly PlanLayer[];
}

export type PlanLayer = PlanRasterLayer | PlanAdjustmentLayer | PlanGroupLayer;

export interface CompileOptions {
  /** 线性空间混合。§4.4：由文档位深与用户设置决定，不硬编码。 */
  readonly linearSpace?: boolean;
}

export interface CompileResult {
  readonly graph: RenderGraph;
  /** 输出节点。 */
  readonly output: NodeId;
  /** 每个图层最终混合进背景的那个 Blend 节点，供测试与调试定位。 */
  readonly blendOf: ReadonlyMap<string, NodeId>;
  /** 各图层的第 3 步（fillOpacity）节点。 */
  readonly fillOpacityOf: ReadonlyMap<string, NodeId>;
  /** 各图层的第 7 步（剪贴）节点。 */
  readonly clipOf: ReadonlyMap<string, NodeId>;
}

/** 编译期状态：节点计数器与各种索引。 */
interface Ctx {
  readonly graph: RenderGraph;
  readonly linearSpace: boolean;
  counter: { value: number };
  readonly blendOf: Map<string, NodeId>;
  readonly fillOpacityOf: Map<string, NodeId>;
  readonly clipOf: Map<string, NodeId>;
}

function fresh(ctx: Ctx, hint: string): NodeId {
  return nodeId(`${hint}#${ctx.counter.value++}`);
}

function add(ctx: Ctx, node: RenderNode): NodeId {
  ctx.graph.add(node);
  return node.id;
}

/**
 * 建一个图层自身的像素链：第 1 步到第 6 步。
 *
 * 停在第 6 步是有意的——第 7 步（剪贴）要看下方的基底，第 8 步要看背景，
 * 两者都不属于"这个图层自己"，由调用方在知道上下文时接上。
 */
function buildLayerPixels(ctx: Ctx, layer: PlanRasterLayer | PlanAdjustmentLayer): NodeId {
  // 第 1 步：取图层像素
  let current = add(ctx, {
    id: fresh(ctx, `src.${layer.id}`),
    kind: 'SourceTile',
    inputs: [],
    layer: layer.id,
  });

  // 第 2 步：智能滤镜链
  for (const filter of layer.filters ?? []) {
    current = add(ctx, {
      id: fresh(ctx, `filter.${layer.id}`),
      kind: 'Effect',
      inputs: [current],
      effect: filter,
      // 滤镜作用在图层像素上，属于"内部"
      placement: 'inner',
    });
  }

  // 第 3 步：fillOpacity。**必须在图层样式之前**——这正是它与 opacity 的区别
  const fill = add(ctx, {
    id: fresh(ctx, `fill.${layer.id}`),
    kind: 'Adjustment',
    inputs: [current],
    adjustment: 'fillOpacity',
    params: { amount: layer.fillOpacity },
  });
  ctx.fillOpacityOf.set(layer.id, fill);
  current = fill;

  // 第 4 步：图层样式
  for (const effect of layer.effects ?? []) {
    current = add(ctx, {
      id: fresh(ctx, `fx.${layer.id}`),
      kind: 'Effect',
      inputs: [current],
      effect: effect.name,
      placement: effect.placement,
    });
  }

  // 第 5 步：mask 与 vectorMask（相乘）
  if (layer.mask !== undefined) {
    const maskSource = add(ctx, {
      id: fresh(ctx, `mask.src.${layer.id}`),
      kind: 'SourceTile',
      inputs: [],
      layer: layer.mask,
    });
    current = add(ctx, {
      id: fresh(ctx, `mask.${layer.id}`),
      kind: 'Mask',
      inputs: [current, maskSource],
    });
  }

  // 第 6 步：blendIf
  if (layer.blendIf === true) {
    current = add(ctx, {
      id: fresh(ctx, `blendif.${layer.id}`),
      kind: 'Adjustment',
      inputs: [current],
      adjustment: 'blendIf',
      params: {},
    });
  }

  return current;
}

/**
 * 合成一组同级图层，自下而上混进 `backdrop`，返回新的背景节点。
 *
 * `backdrop` 是穿透语义的落点：穿透组把**外层背景**传进来，非穿透组传进来的是
 * 组自己的空背景。除此之外两者的处理完全相同——语义差别只在这一个参数上，
 * 这也是把它写成参数而不是两套代码的原因。
 */
function composeChildren(ctx: Ctx, children: readonly PlanLayer[], backdrop: NodeId): NodeId {
  let current = backdrop;
  /**
   * 最近一个非剪贴图层的**混合前**像素。
   *
   * 第 7 步说「与下方基底图层的 alpha 相交」，基底指的是剪贴组底下那个
   * 非剪贴图层，而不是"当前背景"——背景里还叠着更下面的其它图层。
   */
  let clipBase: NodeId | null = null;

  for (const child of children) {
    // 不可见图层整条链都不建：建了再乘 0 是白跑一遍，还会把 draw 数算高
    if (!child.visible) continue;

    let pixels: NodeId;

    if (child.kind === 'group') {
      if (child.passThrough) {
        // 穿透：组内直接以**外层背景**为背景往上叠。组里的调整图层
        // 因此能看到并影响组外的内容
        const inner = composeChildren(ctx, child.children, current);
        // 组本身的 fillOpacity / 样式 / 遮罩仍然作用在合成结果上
        pixels = applyGroupEnvelope(ctx, child, inner);
        // 穿透组已经把自己混进背景了，不再走下面那次 Blend
        current = pixels;
        ctx.blendOf.set(child.id, current);
        clipBase = current;
        continue;
      }
      // 非穿透：组先在**独立的空背景**上合成，再整体混合进来
      const isolated = add(ctx, {
        id: fresh(ctx, `group.base.${child.id}`),
        kind: 'SourceTile',
        inputs: [],
        // 空背景也是一次取样：用一个约定的空图层标识，
        // 让"组从空白开始"在图上是看得见的，而不是隐含在代码里
        layer: ` empty`,
      });
      const inner = composeChildren(ctx, child.children, isolated);
      pixels = applyGroupEnvelope(ctx, child, inner);
    } else {
      pixels = buildLayerPixels(ctx, child);
    }

    // 第 7 步：剪贴
    if (child.clipped) {
      if (clipBase === null) {
        // 最底下的图层不能是剪贴图层——没有可剪贴的基底。
        // 静默忽略会让用户看到一张"少了一层"的图却不知道为什么
        throw new InvalidGraphError(
          `图层「${child.id}」标了剪贴，但它下方没有可作基底的图层。`,
        );
      }
      const clip = add(ctx, {
        id: fresh(ctx, `clip.${child.id}`),
        kind: 'Clip',
        inputs: [pixels, clipBase],
      });
      ctx.clipOf.set(child.id, clip);
      pixels = clip;
    }

    // 第 8 步：以 blendMode + opacity 混合进背景
    current = add(ctx, {
      id: fresh(ctx, `blend.${child.id}`),
      kind: 'Blend',
      inputs: [current, pixels],
      blendMode: child.blendMode,
      opacity: child.opacity,
      linearSpace: ctx.linearSpace,
    });
    ctx.blendOf.set(child.id, current);

    // 剪贴图层不能当基底：连续的剪贴图层都剪到同一个基底上
    if (!child.clipped) clipBase = pixels;
  }

  return current;
}

/**
 * 组自身的 fillOpacity / 样式 / 遮罩。
 *
 * 组也是图层，第 3 到第 6 步一样要走。区别只在于第 1 步的"像素"来自
 * 组内合成的结果，而不是一张贴图。
 */
function applyGroupEnvelope(ctx: Ctx, group: PlanGroupLayer, inner: NodeId): NodeId {
  let current = inner;

  const fill = add(ctx, {
    id: fresh(ctx, `fill.${group.id}`),
    kind: 'Adjustment',
    inputs: [current],
    adjustment: 'fillOpacity',
    params: { amount: group.fillOpacity },
  });
  ctx.fillOpacityOf.set(group.id, fill);
  current = fill;

  for (const effect of group.effects ?? []) {
    current = add(ctx, {
      id: fresh(ctx, `fx.${group.id}`),
      kind: 'Effect',
      inputs: [current],
      effect: effect.name,
      placement: effect.placement,
    });
  }

  if (group.mask !== undefined) {
    const maskSource = add(ctx, {
      id: fresh(ctx, `mask.src.${group.id}`),
      kind: 'SourceTile',
      inputs: [],
      layer: group.mask,
    });
    current = add(ctx, {
      id: fresh(ctx, `mask.${group.id}`),
      kind: 'Mask',
      inputs: [current, maskSource],
    });
  }

  return current;
}

/** 把一份合成计划编译成可执行的 Render Graph。 */
export function compileCompositePlan(
  root: PlanGroupLayer,
  options: CompileOptions = {},
): CompileResult {
  const ctx: Ctx = {
    graph: new RenderGraph(),
    linearSpace: options.linearSpace ?? false,
    counter: { value: 0 },
    blendOf: new Map(),
    fillOpacityOf: new Map(),
    clipOf: new Map(),
  };

  // 画布底色：整张图的最初背景
  const canvas = add(ctx, {
    id: nodeId('canvas'),
    kind: 'SourceTile',
    inputs: [],
    layer: ' canvas',
  });

  const composed = composeChildren(ctx, root.children, canvas);

  const output = add(ctx, {
    id: nodeId('output'),
    kind: 'Output',
    inputs: [composed],
  });

  // 建完就排一次序：环或悬空引用要在这里暴露，而不是等到提交 GPU
  ctx.graph.topologicalOrder();

  return {
    graph: ctx.graph,
    output,
    blendOf: ctx.blendOf,
    fillOpacityOf: ctx.fillOpacityOf,
    clipOf: ctx.clipOf,
  };
}
